import type { WidgetPosition } from "../../../api/myViewController";

export const COLUMNS = 12;
export const ROW_HEIGHT = 111;
export const GAP = 16;

export function overlaps(a: WidgetPosition, b: WidgetPosition) {
	return (
		a.xPos < b.xPos + b.width &&
		a.xPos + a.width > b.xPos &&
		a.yPos < b.yPos + b.height &&
		a.yPos + a.height > b.yPos
	);
}

export function normalize<T extends WidgetPosition>(widget: T): T {
	const width = Math.max(1, Math.min(COLUMNS, widget.width || 4));
	return {
		...widget,
		width,
		height: Math.max(1, Math.min(100, widget.height || 3)),
		xPos: Math.max(0, Math.min(COLUMNS - width, widget.xPos)),
		yPos: Math.max(0, widget.yPos),
	};
}

// Move colliding cards downward. Coordinates remain in the legacy 12-column units.
export function place<T extends WidgetPosition>(widgets: T[], changed: T): T[] {
	const placed = [normalize(changed)];
	const remaining = widgets.filter((w) => w.id !== changed.id).sort((a, b) => a.yPos - b.yPos || a.xPos - b.xPos);
	for (const widget of remaining) {
		let next = normalize(widget);
		let collision = placed.find((p) => overlaps(p, next));
		while (collision) {
			next = { ...next, yPos: collision.yPos + collision.height };
			collision = placed.find((p) => overlaps(p, next));
		}
		placed.push(next);
	}
	return widgets.map((w) => placed.find((p) => p.id === w.id)!);
}

export function arrange<T extends WidgetPosition>(widgets: T[]): T[] {
	const placed: T[] = [];
	for (const widget of widgets) {
		const next = { ...normalize(widget), xPos: 0, yPos: 0 };
		while (placed.some((p) => overlaps(p, next))) {
			next.xPos++;
			if (next.xPos + next.width > COLUMNS) {
				next.xPos = 0;
				next.yPos++;
			}
		}
		placed.push(next);
	}
	return placed;
}
